/**
 * Registration Fields section for Settings > Registration.
 *
 * Lists tenant-scoped RegistrationField definitions (independent of any
 * ProductTemplate) and provides add / edit / delete CRUD via the
 * `/api/v1/registration-fields/` endpoints.
 *
 * Gated by the `registration_fields` feature flag at the parent level.
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  useRegistrationFields,
  useCreateRegistrationField,
  useUpdateRegistrationField,
  useDeleteRegistrationField,
  type RegistrationField,
  type RegistrationFieldType,
} from '@/admin/hooks/use-registration-fields'
import {
  useRegistrationSettings,
  SEGMENT_LABELS,
} from '@/admin/hooks/use-registration-settings'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { extractApiErrorMessage, toastApiError } from '@/shared/lib/api-error'

// ---------------------------------------------------------------------------
// Field-type control set (mirrors product-templates-page.tsx)
// ---------------------------------------------------------------------------

const FIELD_TYPES: RegistrationFieldType[] = [
  'TEXT',
  'RICH_TEXT',
  'NUMBER',
  'BOOLEAN',
  'SELECT',
  'MULTI_SELECT',
  'DATE',
  'DATE_TIME',
  'EMAIL',
  'PHONE',
  'LINK',
  'MONEY',
]

const FIELD_TYPE_LABELS: Record<RegistrationFieldType, string> = {
  TEXT: 'Text',
  RICH_TEXT: 'Rich Text',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
  SELECT: 'Select',
  MULTI_SELECT: 'Multi Select',
  DATE: 'Date',
  DATE_TIME: 'Date Time',
  EMAIL: 'Email',
  PHONE: 'Phone',
  LINK: 'Link',
  MONEY: 'Money',
}

function isSelectType(ft: RegistrationFieldType) {
  return ft === 'SELECT' || ft === 'MULTI_SELECT'
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function RegistrationFieldsSection() {
  const { data, isLoading } = useRegistrationFields()
  const { data: regSettings } = useRegistrationSettings()
  const deleteMutation = useDeleteRegistrationField()
  const wizardSteps = useMemo(
    () => regSettings?.segment_order ?? [],
    [regSettings?.segment_order],
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<RegistrationField | null>(null)
  const [deleting, setDeleting] = useState<RegistrationField | null>(null)

  function openAdd() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(row: RegistrationField) {
    setEditing(row)
    setDialogOpen(true)
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await deleteMutation.mutateAsync(deleting.uuid)
      toast.success(`Deleted "${deleting.display_name}"`)
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete registration field')
    }
  }

  const columns: DataTableColumn<RegistrationField>[] = useMemo(
    () => [
      {
        id: 'display_name',
        header: 'Field',
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-medium text-[var(--ink)]">
              {row.display_name}
            </span>
            <code className="text-[11px] text-[var(--ink-3)]">{row.code_name}</code>
          </div>
        ),
      },
      {
        id: 'field_type',
        header: 'Type',
        cell: (row) => (
          <Badge variant="outline">
            {FIELD_TYPE_LABELS[row.field_type] ?? row.field_type}
          </Badge>
        ),
      },
      {
        id: 'required',
        header: 'Required',
        cell: (row) =>
          row.required ? (
            <Badge variant="default">Required</Badge>
          ) : (
            <span className="text-[11px] text-[var(--ink-3)]">Optional</span>
          ),
      },
      {
        id: 'capture_step',
        header: 'Step',
        cell: (row) => {
          if (!row.capture_step) {
            return <span className="text-[11px] text-[var(--ink-3)]">-</span>
          }
          const missing =
            wizardSteps.length > 0 &&
            !wizardSteps.includes(row.capture_step)
          return (
            <span className="flex items-center gap-1 text-[12px] text-[var(--ink-2)]">
              {SEGMENT_LABELS[row.capture_step] ?? row.capture_step}
              {missing && (
                <AlertTriangle
                  size={12}
                  className="text-[var(--status-warn)]"
                  title="This step is no longer in the wizard order"
                />
              )}
            </span>
          )
        },
      },
      {
        id: 'is_active',
        header: 'Status',
        cell: (row) =>
          row.is_active ? (
            <Badge
              variant="outline"
              className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
            >
              Active
            </Badge>
          ) : (
            <Badge variant="destructive">Inactive</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={(e) => {
                e.stopPropagation()
                openEdit(row)
              }}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={(e) => {
                e.stopPropagation()
                setDeleting(row)
              }}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5 text-[var(--status-error)]" />
            </Button>
          </div>
        ),
      },
    ],
    [wizardSteps],
  )

  return (
    <div className="mb-8">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="clara-section-label">Registration Fields</p>
          <p className="clara-small mt-1">
            Tenant-wide dynamic fields captured on every registration, regardless of
            product. Use these for concerns that apply across your catalogue - e.g.
            installer, structure type, site code.
          </p>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="mr-1.5 h-3 w-3" />
          Add Field
        </Button>
      </div>

      <DataTable<RegistrationField>
        tableId="registration-fields"
        columns={columns}
        data={data ?? []}
        isLoading={isLoading}
        rowKey={(row) => row.uuid}
        emptyMessage="No registration fields defined. Click “Add Field” to create the first one."
      />

      <RegistrationFieldDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v)
          if (!v) setEditing(null)
        }}
        editing={editing}
        existingCodeNames={(data ?? []).map((f) => f.code_name)}
        onSaved={() => {
          setDialogOpen(false)
          setEditing(null)
        }}
      />

      <Dialog
        open={!!deleting}
        onOpenChange={(v) => {
          if (!v) setDeleting(null)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Registration Field</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-[var(--ink-2)]">
            Delete{' '}
            <span className="font-medium text-[var(--ink)]">
              {deleting?.display_name}
            </span>
            ? This cannot be undone. Fields with captured values on existing
            registrations cannot be deleted - mark them inactive instead.
          </p>
          <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Add / edit dialog
// ---------------------------------------------------------------------------

interface RegistrationFieldDialogProps {
  open: boolean
  onOpenChange: (value: boolean) => void
  editing: RegistrationField | null
  existingCodeNames: string[]
  onSaved: () => void
}

// ── Form values + defaults ──────────────────────────────────

const CODE_NAME_REGEX = /^[a-z][a-z0-9_]*$/

type FormValues = {
  code_name: string
  display_name: string
  description: string
  field_type: RegistrationFieldType
  required: boolean
  capture_step: string
  is_active: boolean
  order: number
  options_csv: string
}

function buildDefaults(editing: RegistrationField | null): FormValues {
  const opts = editing?.configuration_data?.options as
    | Array<{ value: string; label?: string } | string>
    | undefined
  const optionsCsv = Array.isArray(opts)
    ? opts.map((o) => (typeof o === 'string' ? o : o.value)).join(', ')
    : ''
  return {
    code_name: editing?.code_name ?? '',
    display_name: editing?.display_name ?? '',
    description: editing?.description ?? '',
    field_type: editing?.field_type ?? 'TEXT',
    required: editing?.required ?? false,
    capture_step: editing?.capture_step ?? '__default__',
    is_active: editing?.is_active ?? true,
    order: editing?.order ?? 0,
    options_csv: optionsCsv,
  }
}

function RegistrationFieldDialog({
  open,
  onOpenChange,
  editing,
  existingCodeNames,
  onSaved,
}: RegistrationFieldDialogProps) {
  const { data: regSettings } = useRegistrationSettings()
  const wizardSteps = useMemo(
    () => regSettings?.segment_order ?? [],
    [regSettings?.segment_order],
  )
  const createMutation = useCreateRegistrationField()
  const updateMutation = useUpdateRegistrationField()

  const isEdit = !!editing
  const initialValues = useMemo(() => buildDefaults(editing), [editing])
  const [submitError, setSubmitError] = useState<string | null>(null)

  const fieldTypeOptions = useMemo(
    () =>
      FIELD_TYPES.map((ft) => ({
        label: FIELD_TYPE_LABELS[ft],
        value: ft,
      })),
    [],
  )

  const stepOptions = useMemo(
    () => [
      { value: '__default__', label: 'Default (end of flow)' },
      ...wizardSteps
        .filter((step) => step !== 'review')
        .map((step) => ({
          value: step,
          label: SEGMENT_LABELS[step] ?? step,
        })),
    ],
    [wizardSteps],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'display_name',
        label: 'Display Name',
        testId: 'rf-display-name',
        placeholder: 'Installer',
        className: 'col-span-1',
        value: initialValues.display_name,
        validationSchema: z.string().trim().min(1, 'Display name is required.'),
      },
      {
        type: 'text',
        name: 'code_name',
        label: 'Code Name',
        testId: 'rf-code-name',
        placeholder: 'installer',
        className: 'col-span-1',
        value: initialValues.code_name,
        validationSchema: z.string()
          .trim()
          .min(1, 'Code name is required.')
          .regex(
            CODE_NAME_REGEX,
            'Code name must be lowercase, start with a letter, and contain only letters, digits, and underscores.',
          )
          .refine((value) => {
            const trimmed = (value ?? '').trim()
            if (!trimmed) return true
            const taken = existingCodeNames.includes(trimmed)
            if (!taken) return true
            return isEdit && trimmed === editing?.code_name
          }, 'A field with this code name already exists.'),
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional hint shown to the person filling the form',
        className: 'col-span-2',
        value: initialValues.description,
      },
      {
        type: 'select',
        name: 'field_type',
        label: 'Field Type',
        className: 'col-span-1',
        value: initialValues.field_type,
        options: fieldTypeOptions,
      },
      {
        type: 'number',
        name: 'order',
        label: 'Display Order',
        className: 'col-span-1',
        value: initialValues.order,
      },
      {
        type: 'text',
        name: 'options_csv',
        label: 'Options (comma-separated)',
        placeholder: 'option_one, option_two, option_three',
        hint: 'Each entry becomes both the stored value and the label shown to users.',
        className: 'col-span-2',
        value: initialValues.options_csv,
        visible: (formContext) =>
          isSelectType(formContext.values.field_type as RegistrationFieldType),
        // Conditional "required when field type is select" depends on a
        // sibling value, which a per-field schema can't read; enforced
        // server-side.
        validationSchema: z.string().optional(),
      },
      {
        type: 'select',
        name: 'capture_step',
        label: 'Capture at Step',
        hint: 'Which wizard step should capture this field. Steps are defined in Step Order above.',
        className: 'col-span-2',
        value: initialValues.capture_step,
        options: stepOptions,
      },
      {
        type: 'custom',
        name: '__capture_step_warning',
        className: 'col-span-2',
        value: '',
        visible: (formContext) => {
          const captureStep = formContext.values.capture_step as string | null | undefined
          return (
            !!captureStep &&
            captureStep !== '__default__' &&
            wizardSteps.length > 0 &&
            !wizardSteps.includes(captureStep)
          )
        },
        render: (formContext) => {
          const captureStep = formContext.values.capture_step as string | null | undefined
          return (
            <div className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-3 py-2">
              <AlertTriangle
                size={13}
                className="mt-px shrink-0 text-[var(--status-warn)]"
              />
              <span className="text-[11px] text-[var(--status-warn)]">
                The step &ldquo;
                {SEGMENT_LABELS[captureStep!] ?? captureStep}
                &rdquo; is no longer in the wizard order. This field won&rsquo;t be
                captured until the step is re-added or you select a different step.
              </span>
            </div>
          )
        },
      },
      {
        type: 'custom',
        name: 'required',
        className: 'col-span-2',
        value: initialValues.required,
        render: (formContext) => (
          <ToggleRow
            label="Required"
            hint="Value must be provided at submission."
            checked={Boolean(formContext.values.required)}
            onChange={(next) => formContext.setFieldValue('required', next)}
          />
        ),
      },
      {
        type: 'custom',
        name: 'is_active',
        className: 'col-span-2',
        value: initialValues.is_active,
        render: (formContext) => (
          <ToggleRow
            label="Active"
            hint="Inactive fields are hidden from new registrations."
            checked={Boolean(formContext.values.is_active)}
            onChange={(next) => formContext.setFieldValue('is_active', next)}
          />
        ),
      },
      {
        type: 'custom',
        name: '__submit_error',
        className: 'col-span-2',
        value: '',
        visible: () => Boolean(submitError),
        render: () => (
          <p className="text-[12px] text-[var(--status-error)]">{submitError}</p>
        ),
      },
    ],
    [
      editing?.code_name,
      existingCodeNames,
      fieldTypeOptions,
      initialValues.capture_step,
      initialValues.code_name,
      initialValues.description,
      initialValues.display_name,
      initialValues.field_type,
      initialValues.is_active,
      initialValues.options_csv,
      initialValues.order,
      initialValues.required,
      isEdit,
      stepOptions,
      submitError,
      wizardSteps,
    ],
  )

  const { Form, FormFooter } = useForm(
    {
      name: 'registration-field-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      setSubmitError(null)
      const values = rawValues as unknown as FormValues
      const options = isSelectType(values.field_type)
        ? values.options_csv
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .map((value) => ({ value, label: value }))
        : undefined
      // Keep the settings this dialog does not edit (visibility, lookups,
      // grouping). A field's settings are shared with every attribute linked
      // to it, so dropping a key here drops it on all of them.
      const keptConfig: Record<string, unknown> = {
        ...(editing?.configuration_data ?? {}),
      }
      delete keptConfig.options

      const payload = {
        code_name: values.code_name.trim(),
        display_name: values.display_name.trim(),
        description: (values.description ?? '').trim(),
        field_type: values.field_type,
        required: values.required,
        capture_step:
          values.capture_step && values.capture_step !== '__default__'
            ? values.capture_step
            : null,
        is_active: values.is_active,
        order: Number(values.order) || 0,
        configuration_data: options ? { ...keptConfig, options } : keptConfig,
      }

      try {
        if (isEdit) {
          await updateMutation.mutateAsync({ uuid: editing!.uuid, ...payload })
          toast.success('Registration field updated')
        } else {
          await createMutation.mutateAsync(payload)
          toast.success('Registration field created')
        }
        onSaved()
      } catch (err) {
        setSubmitError(
          extractApiErrorMessage(err) ?? 'Failed to save registration field',
        )
      }
    },
    [open, editing?.uuid, fields],
  )

  const busy = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) setSubmitError(null)
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Registration Field' : 'Add Registration Field'}
          </DialogTitle>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-3 py-2" />
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={busy ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Field'}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Small reusable toggle row (keeps dialog markup tidy)
// ---------------------------------------------------------------------------

interface ToggleRowProps {
  label: string
  hint: string
  checked: boolean
  onChange: (value: boolean) => void
}

function ToggleRow({ label, hint, checked, onChange }: ToggleRowProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => onChange(!checked)}
      className="flex h-auto w-full items-center justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
    >
      <Switch checked={checked} decorative size="sm" />
      <span className="flex flex-1 flex-col">
        <span className="text-[12px] font-medium text-[var(--ink)]">{label}</span>
        <span className="mt-0.5 text-[11px] text-[var(--ink-3)]">{hint}</span>
      </span>
    </Button>
  )
}
